import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, fmtErr } from "../../lib/api";

const EMPTY = {
  address_street: "", unit_number: "", city: "Vancouver", province: "BC", postal_code: "", neighborhood: "",
  latitude: "", longitude: "", hide_exact_address: false, floor: "", square_feet: "", beds: 1, baths: 1,
  building_type: "apartment", year_built: "", outdoor_space: "none", view: "", monthly_rent: "", currency: "CAD",
  promotions: "", deposit_amount: "", application_fee: "", utilities_included: "", date_available: "",
  minimum_lease_months: 12, lease_type: "either", parking: "none", parking_details: "", pet_friendly: "na",
  furnished: "na", laundry: "na", heating_cooling: "", accessibility: "na", accessibility_details: "",
  amenities: "", smoking_allowed: "na", images: "", floor_plan_images: "", virtual_tour_url: "",
  company_type: "na", company_name: "", landlord_contact_name: "", landlord_contact_email: "",
  landlord_contact_phone: "", referral_status: "unknown", referral_terms: "", description: "",
  internal_notes: "", status: "active", source_name: "Direct", source_url: "",
  plan: "", payment_status: "", payment_link: "", free_trial_start: "", free_trial_end: "", published: true,
};

export default function ListingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    api.get("/admin/companies").then((r) => setCompanies(r.data)).catch(() => {});
    if (id) {
      api.get(`/admin/listings`, { params: {} }).then((r) => {
        const l = r.data.find((x) => x.listing_id === id);
        if (l) setForm({
          ...EMPTY, ...l,
          images: (l.images || []).map((i) => i.url).join("\n"),
          floor_plan_images: (l.floor_plan_images || []).join("\n"),
          amenities: (l.amenities || []).join(", "),
          utilities_included: (l.utilities_included || []).join(", "),
        });
      }).catch((e) => toast.error(fmtErr(e)));
    }
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.address_street || !form.monthly_rent) return toast.error("Address and rent are required.");
    setBusy(true);
    const payload = { ...form };
    for (const k of ["beds", "baths", "square_feet", "monthly_rent", "latitude", "longitude", "floor", "year_built", "minimum_lease_months", "deposit_amount", "application_fee"]) {
      payload[k] = payload[k] === "" || payload[k] === null ? null : Number(payload[k]);
    }
    payload.images = form.images.split("\n").map((u) => u.trim()).filter(Boolean).map((u) => ({ url: u, alt: `${form.beds}-bedroom rental in ${form.neighborhood || form.city}` }));
    payload.floor_plan_images = form.floor_plan_images.split("\n").map((u) => u.trim()).filter(Boolean);
    payload.amenities = form.amenities.split(",").map((a) => a.trim()).filter(Boolean);
    payload.utilities_included = form.utilities_included.split(",").map((a) => a.trim()).filter(Boolean);
    if (form.company_name) {
      const comp = companies.find((c) => c.name === form.company_name);
      if (comp) { payload.company_id = comp.company_id; payload.company_type = comp.type; }
    }
    try {
      if (id) { await api.put(`/admin/listings/${id}`, payload); toast.success("Listing updated"); }
      else {
        const r = await api.post("/admin/listings", payload);
        toast.success(r.data.action === "added" ? "Listing added" : "Merged with an existing listing (duplicate detected)");
      }
      navigate("/dashboard/inventory");
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="listing-form" className="max-w-4xl">
      <Link to="/dashboard/inventory" className="text-sm text-muted-foreground hover:text-primary">← Back to inventory</Link>
      <h1 className="mt-3 font-heading font-bold text-2xl tracking-tight">{id ? "Edit listing" : "Add listing"}</h1>

      <Section title="Location">
        <F label="Street address *" k="address_street" form={form} set={set} tid="lf-address" />
        <F label="Unit #" k="unit_number" form={form} set={set} tid="lf-unit" />
        <F label="City" k="city" form={form} set={set} tid="lf-city" />
        <F label="Province" k="province" form={form} set={set} tid="lf-province" />
        <F label="Postal code" k="postal_code" form={form} set={set} tid="lf-postal" />
        <F label="Neighbourhood" k="neighborhood" form={form} set={set} tid="lf-neighborhood" />
        <F label="Latitude" k="latitude" type="number" form={form} set={set} tid="lf-lat" />
        <F label="Longitude" k="longitude" type="number" form={form} set={set} tid="lf-lng" />
        <label className="flex items-center gap-2 text-sm col-span-2">
          <input data-testid="lf-hide-address" type="checkbox" checked={form.hide_exact_address} onChange={(e) => set("hide_exact_address", e.target.checked)} className="accent-[#0D5C63]" />
          Hide exact address publicly
        </label>
      </Section>

      <Section title="Physical">
        <F label="Bedrooms (0 = studio)" k="beds" type="number" form={form} set={set} tid="lf-beds" />
        <F label="Bathrooms" k="baths" type="number" form={form} set={set} tid="lf-baths" step="0.5" />
        <F label="Square feet" k="square_feet" type="number" form={form} set={set} tid="lf-sqft" />
        <F label="Floor" k="floor" type="number" form={form} set={set} tid="lf-floor" />
        <S label="Building type" k="building_type" form={form} set={set} tid="lf-building-type" opts={["apartment", "condo", "townhouse", "house", "basement suite", "laneway", "room"]} />
        <F label="Year built" k="year_built" type="number" form={form} set={set} tid="lf-year" />
        <S label="Outdoor space" k="outdoor_space" form={form} set={set} tid="lf-outdoor" opts={["none", "balcony", "patio", "yard"]} />
        <F label="View" k="view" form={form} set={set} tid="lf-view" />
      </Section>

      <Section title="Pricing & terms">
        <F label="Monthly rent (CAD) *" k="monthly_rent" type="number" form={form} set={set} tid="lf-rent" />
        <F label="Promotions" k="promotions" form={form} set={set} tid="lf-promotions" />
        <F label="Deposit amount" k="deposit_amount" type="number" form={form} set={set} tid="lf-deposit" />
        <F label="Application fee" k="application_fee" type="number" form={form} set={set} tid="lf-appfee" />
        <F label="Utilities included (comma-sep)" k="utilities_included" form={form} set={set} tid="lf-utilities" />
        <F label="Date available" k="date_available" type="date" form={form} set={set} tid="lf-available" />
        <F label="Minimum lease (months)" k="minimum_lease_months" type="number" form={form} set={set} tid="lf-min-lease" />
        <S label="Lease type" k="lease_type" form={form} set={set} tid="lf-lease-type" opts={["fixed", "month-to-month", "either"]} />
      </Section>

      <Section title="Features">
        <S label="Parking" k="parking" form={form} set={set} tid="lf-parking" opts={["none", "included", "available_extra_cost"]} />
        <F label="Parking details" k="parking_details" form={form} set={set} tid="lf-parking-details" />
        <S label="Pet friendly" k="pet_friendly" form={form} set={set} tid="lf-pets" opts={["yes", "no", "cats_only", "dogs_only", "case_by_case", "na"]} />
        <S label="Furnished" k="furnished" form={form} set={set} tid="lf-furnished" opts={["furnished", "unfurnished", "partially", "na"]} />
        <S label="Laundry" k="laundry" form={form} set={set} tid="lf-laundry" opts={["in_unit", "shared", "none", "na"]} />
        <F label="Heating/cooling" k="heating_cooling" form={form} set={set} tid="lf-heating" />
        <S label="Accessibility" k="accessibility" form={form} set={set} tid="lf-accessibility" opts={["accessible", "not_accessible", "na"]} />
        <F label="Accessibility details" k="accessibility_details" form={form} set={set} tid="lf-accessibility-details" />
        <F label="Amenities (comma-sep)" k="amenities" form={form} set={set} tid="lf-amenities" wide />
        <S label="Smoking" k="smoking_allowed" form={form} set={set} tid="lf-smoking" opts={["yes", "no", "na"]} />
      </Section>

      <Section title="Media">
        <Area label="Image URLs (one per line)" k="images" form={form} set={set} tid="lf-images" />
        <Area label="Floor plan image URLs (one per line)" k="floor_plan_images" form={form} set={set} tid="lf-floorplans" />
        <F label="Virtual tour URL" k="virtual_tour_url" form={form} set={set} tid="lf-tour" />
      </Section>

      <Section title="Ownership & contact (internal only)">
        <div>
          <label className="block text-xs font-semibold mb-1.5" htmlFor="lf-company">Company</label>
          <select id="lf-company" data-testid="lf-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm">
            <option value="">— none —</option>
            {companies.map((c) => <option key={c.company_id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <F label="Contact name" k="landlord_contact_name" form={form} set={set} tid="lf-contact-name" />
        <F label="Contact email" k="landlord_contact_email" type="email" form={form} set={set} tid="lf-contact-email" />
        <F label="Contact phone" k="landlord_contact_phone" form={form} set={set} tid="lf-contact-phone" />
        <S label="Referral status" k="referral_status" form={form} set={set} tid="lf-referral-status" opts={["unknown", "has_referral_program", "no_referral_program", "in_discussion", "partner"]} />
        <F label="Referral terms" k="referral_terms" form={form} set={set} tid="lf-referral-terms" />
      </Section>

      <Section title="Plan & publishing (internal — never shown to renters)">
        <S label="Plan" k="plan" form={form} set={set} tid="lf-plan" opts={["", "per_listing", "starter", "growth", "unlimited"]} />
        <S label="Payment status" k="payment_status" form={form} set={set} tid="lf-payment-status" opts={["", "free_trial", "payment_confirmed", "unpaid"]} />
        <F label="Payment link (sent to client)" k="payment_link" form={form} set={set} tid="lf-payment-link" />
        <F label="Free trial start" k="free_trial_start" type="date" form={form} set={set} tid="lf-trial-start" />
        <F label="Free trial end" k="free_trial_end" type="date" form={form} set={set} tid="lf-trial-end" />
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="lf-published" type="checkbox" checked={!!form.published} onChange={(e) => set("published", e.target.checked)} className="accent-[#0D5C63]" />
          Published (visible on the public site)
        </label>
      </Section>

      <Section title="Meta">
        <Area label="Description" k="description" form={form} set={set} tid="lf-description" rows={4} />
        <Area label="Internal notes (agents only)" k="internal_notes" form={form} set={set} tid="lf-notes" />
        <S label="Status" k="status" form={form} set={set} tid="lf-status" opts={["active", "pending", "expired", "removed", "duplicate"]} />
        <F label="Source name" k="source_name" form={form} set={set} tid="lf-source-name" />
        <F label="Source URL" k="source_url" form={form} set={set} tid="lf-source-url" />
      </Section>

      <div className="mt-8 flex gap-3">
        <button data-testid="listing-save" onClick={submit} disabled={busy}
          className="rounded-full bg-primary text-primary-foreground px-8 py-3 font-semibold disabled:opacity-50 inline-flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {id ? "Save changes" : "Add listing"}
        </button>
        <Link to="/dashboard/inventory" className="rounded-full border border-border px-8 py-3 font-semibold text-sm">Cancel</Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8 rounded-2xl border border-border bg-card p-6">
      <h2 className="font-heading font-semibold text-lg mb-4">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function F({ label, k, form, set, type = "text", tid, wide, step }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-semibold mb-1.5" htmlFor={tid}>{label}</label>
      <input id={tid} data-testid={tid} type={type} step={step} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)}
        className="w-full rounded-xl border border-input bg-background px-4 py-2 text-sm" />
    </div>
  );
}

function S({ label, k, form, set, opts, tid }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5" htmlFor={tid}>{label}</label>
      <select id={tid} data-testid={tid} value={form[k]} onChange={(e) => set(k, e.target.value)}
        className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm">
        {opts.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
      </select>
    </div>
  );
}

function Area({ label, k, form, set, tid, rows = 3 }) {
  return (
    <div className="sm:col-span-2">
      <label className="block text-xs font-semibold mb-1.5" htmlFor={tid}>{label}</label>
      <textarea id={tid} data-testid={tid} rows={rows} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)}
        className="w-full rounded-xl border border-input bg-background px-4 py-2 text-sm" />
    </div>
  );
}
